/**
 * Feature modules of the web app: each web/src/modules/<name>/index.jsx exports
 *
 *   export default {
 *     name: 'invoices',
 *     publicRoutes: [{ path: 'store', lazy: () => import('./Store.jsx').then((m) => ({ Component: m.Store })) }],
 *     clientRoutes: [{ path: 'invoices', lazy: … }],     // need a signed-in client
 *     adminRoutes: [{ path: 'invoices', lazy: … }],      // under /admin, need staff
 *     clientNav: [{ key: 'invoices', to: '/invoices', label: 'Invoices', order: 40 }],
 *     adminNav: [{ section: 'billing', to: '/admin/invoices', label: 'Invoices', permission: 'admin.invoices.read', order: 10 }],
 *     clientWidgets: [{ key: 'due', order: 20, Component }],   // client dashboard
 *     adminWidgets: [{ key: 'income', order: 10, permission: 'admin.reports.read', Component }],
 *     adminClientTabs: [{ key: 'invoices', label: 'Invoices', order: 30, permission, Component }], // ({ client })
 *   };
 *
 * Route paths are relative (no leading slash). Pages load lazily so each module is its own chunk.
 * Addons (addons/<name>/web/index.jsx at the repository root, docs/dev/addons.md) have the same shape;
 * the backend decides whether an addon is switched on: while it is off its nav entries and widgets
 * are hidden (addonOn) and its pages get 404 from the API.
 */
const found = {
  ...import.meta.glob('./modules/*/index.jsx', { eager: true }),
  ...import.meta.glob('../../addons/*/web/index.jsx', { eager: true }),
};

// An addon's entries carry `addon: <name>`, so the shells can hide them while it is switched off.
export const MODULES = Object.entries(found)
  .filter(([, m]) => m.default)
  .map(([path, m]) => (path.startsWith('../../addons/') ? { ...m.default, addon: true } : m.default));

const collect = (key) =>
  MODULES.flatMap((m) => (m[key] ?? []).map((entry) => (m.addon ? { ...entry, addon: m.name } : entry)));

/**
 * Whether a nav entry or widget shows: a built-in one always, an addon's only while the API runs that
 * addon (`running`: `addons` from GET /api/public/bootstrap, i.e. useAuth().addons).
 */
export const addonOn = (entry, running) => !entry.addon || Boolean(running?.includes(entry.addon));
const byOrder = (a, b) => (a.order ?? 100) - (b.order ?? 100);

export const publicRoutes = () => collect('publicRoutes');
export const clientRoutes = () => collect('clientRoutes');
export const adminRoutes = () => collect('adminRoutes');
export const clientNav = () => collect('clientNav').sort(byOrder);
export const adminNav = () => collect('adminNav').sort(byOrder);
export const clientWidgets = () => collect('clientWidgets').sort(byOrder);
export const adminWidgets = () => collect('adminWidgets').sort(byOrder);
export const adminClientTabs = () => collect('adminClientTabs').sort(byOrder);

/**
 * Admin sidebar sections, in this order: the daily work first, setup below. A module's adminNav entry
 * names one of these (an unknown one lands under System). `flat` sections have no header: their pages
 * sit right under Dashboard.
 */
export const ADMIN_SECTIONS = [
  { key: 'reports', label: 'Reports', flat: true },
  { key: 'clients', label: 'Clients' },
  { key: 'billing', label: 'Billing' },
  { key: 'services', label: 'Services' },
  { key: 'domains', label: 'Domains' },
  { key: 'support', label: 'Support' },
  { key: 'catalog', label: 'Catalog' },
  { key: 'content', label: 'Content' },
  { key: 'billing_settings', label: 'Billing settings' },
  { key: 'staff', label: 'Staff' },
  { key: 'system', label: 'System' },
  { key: 'integrations', label: 'Integrations' },
];
